import {Component, OnDestroy, OnInit} from '@angular/core';
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatListItem, MatListModule, MatNavList} from "@angular/material/list";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {DatePipe} from "@angular/common";
import {Period} from "../../../../core/interfaces/period.interface";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {provideNativeDateAdapter} from "@angular/material/core";
import {MatIconModule} from "@angular/material/icon";
import {Observable, Subscription} from "rxjs";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {LanguageService} from "../../../../shared/services/language.service";
import {DialogConfirmComponent} from "../../../../shared/components/dialogs/dialog-confirm/dialog-confirm.component";
import {MatDialog} from "@angular/material/dialog";

@Component({
  selector: 'app-periods-list-admin',
  standalone: true,
  imports: [
    CardsBasicComponent,
    FormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatListItem,
    MatNavList,
    MatProgressSpinner,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatIconModule,
    MatIconButton,
    TranslateModule
  ],
  providers: [provideNativeDateAdapter(), DatePipe],
  templateUrl: './period-list-admin.component.html',
  styleUrl: './period-list-admin.component.css'
})
export class PeriodListAdminComponent implements OnInit, OnDestroy {
  periodListData: any[] = [];
  isFormVisible: boolean = false;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  period!: Period;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    private _datePipe: DatePipe,
    private _languageService: LanguageService,
    private _translate: TranslateService,
    private _dialog: MatDialog) {
  }

  /**
   * Initializes the component.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.setupLanguageSubscription();
    this.loadAnalyticPeriodsList()
    this.period = this.createEmptyPeriod();
  }

  /**
   * Manages the subscriptions for language changes.
   *
   * @private
   * @returns {void}
   */
  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }

  /**
   * Loads the analytic periods list from the API service and assigns it to the component's periodListData property.
   *
   * @return {void} - This method does not return anything.
   */
  loadAnalyticPeriodsList() {
    this.subscriptions.add(
      this._apiService.fetchPeriodList().subscribe({
        next: (response: any) => {
          this.periodListData = response;
        },
        error: (error) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  /**
   * Creates an empty Period object with empty translations for both English and German.
   *
   * @returns {Period} The newly created empty Period object.
   */
  private createEmptyPeriod(): Period {
    const emptyTranslations = {
      en: {title: ''},
      de: {title: ''}
    };
    return new Period(emptyTranslations, '', '');
  }

  /**
   * Toggles the visibility of a form and resets the form data.
   *
   * @return {void}
   */
  toggleFormVisibility(): void {
    this.isFormVisible = !this.isFormVisible;
    this.resetFormData();
  }

  /**
   * Resets the form data.
   *
   * @returns {void}
   */
  private resetFormData(): void {
    this.period = this.createEmptyPeriod();
    this.isEditMode = false;
  }

  /**
   * Formats a given date to a string in the format 'yyyy-MM-dd'.
   *
   * @param {Date} date - The date to be formatted.
   * @return {string} The formatted date string.
   */
  private formatDate(date: Date): string {
    return this._datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  /**
   * Saves the period either by updating or creating it, based on the edit mode.
   * @returns {void}
   */
  savePeriod(): void {
    if (this.isEditMode) {
      this.updatePeriod();
    } else {
      this.createPeriod();
    }
  }

  /**
   * Applies date formatting to a given period object.
   *
   * @param {Period} period - The period object to apply date formatting to.
   * @returns {Period} - The transformed period object with formatted start_date and end_date properties.
   */
  private applyDateFormatting(period: Period): Period {
    return {
      ...period,
      start_date: this.formatDate(new Date(period.start_date!)),
      end_date: this.formatDate(new Date(period.end_date!))
    };
  }

  /**
   * Handles the subscription to the provided operation.
   *
   * @param {Observable<any>} operation - The operation to subscribe to.
   * @return {void}
   */
  private handleSubscription(operation: Observable<any>): void {
    this.isLoading = true;
    this.subscriptions.add(
      operation.subscribe({
        next: (response: any) => {
          this.isFormVisible = false;
          this.isLoading = false;
          this._snackbarService.show(response.message);
          this.loadAnalyticPeriodsList();
        },
        error: (error) => {
          console.error('There was an error', error);
          this.isLoading = false;
        }
      })
    );
  }

  /**
   * Creates a period by calling the API service with the formatted period value.
   *
   * @returns {void}
   */
  createPeriod(): void {
    const formattedPeriod = this.applyDateFormatting(this.period);
    this.handleSubscription(
      this._apiService.createPeriod(formattedPeriod)
    );
  }

  /**
   * Update the period by calling the API service's updatePeriod method.
   * Format the period and handle the subscription.
   *
   * @returns {void}
   */
  updatePeriod(): void {
    const formattedPeriod = this.applyDateFormatting(this.period);
    this.handleSubscription(
      this._apiService.updatePeriod(this.period.id!, formattedPeriod)
    );
  }

  confirmDelete(id: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    DialogConfirmComponent.openDeleteDialog(this._dialog, this._translate).subscribe(result => {
      if (result) {
        this.deletePeriod(id);
      }
    });
  }

  /**
   * Deletes a period with the specified id.
   *
   * @param {any} id - The id of the period to delete.
   * @return {void}
   */
  deletePeriod(id: any): void {
    this.handleSubscription(
      this._apiService.deletePeriod(id)
    );
  }

  /**
   * Edits the given period.
   *
   * @param {Period} period - The period to be edited.
   * @return {void}
   */
  editPeriod(period: Period): void {
    this.period = {...period};
    this.isFormVisible = true;
    this.isEditMode = true;
  }

  /**
   * Unsubscribes from all subscriptions in the component.
   *
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

}

